import React, { useEffect, useRef } from "react";
import { MOTION } from "../styles/motionTokens";
import { Z } from "../styles/zLayers";
import ParticleBurst from "../styles/ParticleBurst";
import { prefersReducedMotion } from "../styles/particleField";
import CardImage from "../cards/components/card/CardImage";
import { getRevealIntensity } from "./revealIntensity";
import { useDismissible } from "../native/dismissibleStack";

/**
 * CardRevealMoment — the beat when a card joins your collection.
 *
 * Fires on EVERY add, not just the first (FirstCardCelebration still owns that
 * one, and takes precedence). Intensity scales with rarity, so the moment
 * carries information rather than just noise: a chase card gets foil and a held
 * beat, a Rare gets a quick sparkle, a Common gets no overlay at all.
 *
 * Self-dismissing and tap-anywhere-to-close — this sits between the user and
 * the next card they want to add, so it must never feel like a wall.
 */
const CardRevealMoment = ({ open, card, ownerPhotoUrl, onClose }) => {
  const intensity = getRevealIntensity(card);
  const shouldOpen = open && !!card && intensity.showOverlay;
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!shouldOpen) return undefined;
    const t = setTimeout(() => onClose?.(), intensity.holdMs);
    return () => clearTimeout(t);
  }, [shouldOpen, onClose, intensity.holdMs]);

  // A celebration that auto-dismisses is still something Back should skip, not
  // something Back should exit the app past.
  useDismissible(shouldOpen, onClose, "card-reveal-moment");

  /**
   * Focus management, because this really is an `aria-modal` dialog.
   *
   * It declares `aria-modal="true"`, which asks assistive tech to treat
   * everything outside it as hidden, and then moved focus nowhere — so a
   * keyboard or screen-reader user stayed parked on whatever they were standing
   * on when the card was added, a node this surface had just declared
   * invisible. Same remedy and same shape as the sibling this pattern was
   * settled on, `TradeCompletionMoment` (effect at :75-80, `tabIndex={-1}` at
   * :95), which took it in turn from `ProfileAccountSheet` and
   * `MatchRevealMoment`.
   *
   * KEYED ON `shouldOpen`, NOT ON `open` — but NOT for the reason an earlier
   * draft of this comment gave, which a skeptic measured and killed. It claimed
   * that keying on `open` would "fire against a null ref on every Common". That
   * is not a harm: `dialogRef.current?.focus()` optional-chains to a silent
   * no-op, and re-keying the effect to `open` leaves all five of this
   * component's tests green. Both spellings are correct. `shouldOpen` is kept
   * because it is the condition that actually governs whether a dialog exists,
   * so it says what it means — NOT because `open` would break. Nothing pins
   * this choice and nothing should; it is style, not behaviour.
   *
   * FOCUS-IN ONLY. The restore half is absent because this opens from the tail
   * of an add and the opener is gone: the one production path is
   * `ListCardModal`, a MUI `Dialog` that calls `revealCard()` before
   * `handleClose()`, so the Dialog is closing in the same commit this mounts.
   * That was measured on this component specifically — this one DOES win focus
   * in production, unlike `TradeProposedMoment`, whose sibling comment records
   * why it does not.
   *
   * THIS IS NOT A FOCUS TRAP AND MUST NOT BE READ AS ONE. Moving focus in is
   * the announce fix; containing Tab is a separate piece of work and is filed
   * rather than claimed here.
   */
  useEffect(() => {
    // UNPINNABLE BY CONSTRUCTION, and kept anyway — `[#103-MORE-4]`. The
    // component early-returns `null` when closed (just below), so the effect
    // body could not find a ref to focus even with this line deleted: there is
    // no input that makes its absence observable, and a mutant that removes it
    // survives the whole suite. It is defensive symmetry with the render guard,
    // NOT a tested arm — do not read the suite's green as covering it. The
    // `does not touch focus while closed` test passes with or without it.
    if (!shouldOpen) return;
    // The root is a <div>, so this only does anything because of the
    // `tabIndex={-1}` below; without it focus() is a silent no-op.
    dialogRef.current?.focus();
  }, [shouldOpen]);

  if (!shouldOpen) return null;

  const reduced = prefersReducedMotion();
  const isChase = intensity.tier === "chase";
  // The owner photo lives on a different host from the catalogue art (Supabase
  // vs images.pokemontcg.io), so it can die on its own. When it does, this
  // card's own art is the honest substitute — CardImage's global default is a
  // real card (Base Set #1) and would show the wrong Pokemon under the right
  // name. Same OUTCOME as the tile (Card.jsx:221) and the sheet
  // (CardDetailModal.jsx:305), reached slightly differently: both of those fold
  // the placeholder into `stockImg`, while here an absent `stockImg` passes
  // `undefined` and lets CardImage's own default parameter apply.
  //
  // The ternary is load-bearing, not defensive. With no owner photo `src` would
  // already BE `stockImg`, and naming it as the fallback too would leave
  // `resolvedSrc` unchanged on failure: React never reassigns the attribute, no
  // second error can fire, and the image sits at opacity 0 behind a permanent
  // shimmer. That degenerate case is shared with the tile and the sheet and is
  // only unreachable because #295 stopped the "+" path passing catalogue art as
  // an owner photo.
  const stockImg = card?.images?.small || card?.images?.large;
  const imgSrc = ownerPhotoUrl || stockImg;

  return (
    <div
      ref={dialogRef}
      data-testid="card-reveal-moment"
      data-reveal-tier={intensity.tier}
      role="dialog"
      aria-modal="true"
      aria-label={`${card?.name || "Card"} added to your collection`}
      // A <div> is not focusable without this, so the effect above would call
      // focus() to no effect and leave the user exactly where they were. -1 so
      // the root can hold focus without becoming a Tab stop of its own.
      tabIndex={-1}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: Z.celebration,
        background: isChase
          ? "radial-gradient(circle at 50% 42%, rgba(76,29,149,0.6) 0%, rgba(15,23,42,0.94) 55%, rgba(2,6,23,0.97) 100%)"
          : "rgba(15,23,42,0.78)",
        backdropFilter: "blur(7px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily: "Inter, sans-serif",
        animation: reduced ? undefined : "revealFadeIn 220ms ease-out both",
      }}
    >
      <div
        style={{
          position: "relative",
          width: 190,
          height: 262,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 22,
        }}
      >
        {intensity.particleCount > 0 && (
          <ParticleBurst
            variant={intensity.particleVariant}
            count={intensity.particleCount}
            seed={17}
          />
        )}

        <div
          data-testid="card-reveal-art"
          style={{
            width: 168,
            height: 235,
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: intensity.glow
              ? `0 0 ${isChase ? 52 : 30}px rgba(167,139,250,${isChase ? 0.8 : 0.5}), 0 20px 46px rgba(0,0,0,0.5)`
              : "0 16px 36px rgba(0,0,0,0.45)",
            border: "1.5px solid rgba(196,181,253,0.45)",
            animation: reduced
              ? undefined
              : `revealSlot ${MOTION.binderSnapMs}ms cubic-bezier(0.18, 0.9, 0.24, 1.1) both`,
          }}
        >
          <CardImage
            src={imgSrc}
            alt={card?.name || "Card"}
            radius={12}
            style={{ objectFit: "cover" }}
            fallbackSrc={ownerPhotoUrl ? stockImg : undefined}
          />
        </div>
      </div>

      {intensity.label && (
        <div
          data-testid="card-reveal-rarity"
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: isChase ? "#fde047" : "#c4b5fd",
            textShadow: isChase ? "0 0 18px rgba(253,224,71,0.6)" : "none",
            marginBottom: 6,
          }}
        >
          {intensity.label}
        </div>
      )}

      <div
        style={{
          fontSize: isChase ? 24 : 20,
          fontWeight: 900,
          color: "#fff",
          textAlign: "center",
          textShadow: intensity.glow ? "0 0 24px rgba(167,139,250,0.8)" : "none",
          animation: reduced ? undefined : "revealTextIn 420ms ease-out 180ms both",
        }}
      >
        {card?.name || "Card added"}
      </div>
      <div
        style={{
          marginTop: 5,
          fontSize: 13,
          color: "#94a3b8",
          textAlign: "center",
          animation: reduced ? undefined : "revealTextIn 420ms ease-out 280ms both",
        }}
      >
        Added to your collection
      </div>

      <style>{`
        @keyframes revealFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes revealSlot {
          0%   { transform: translateY(46px) scale(0.82) rotate(-5deg); opacity: 0; }
          65%  { transform: translateY(-6px) scale(1.04) rotate(1.5deg); opacity: 1; }
          100% { transform: translateY(0) scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes revealTextIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CardRevealMoment;
